<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import BarChart from 'shared/components/charts/BarChart.vue';
import TableHeaderCell from './TableHeaderCell.vue';
import { formatDuration } from './commercialFormatters';
import { barDataset, barOptions } from '../charts/chartDesign';
import { useChartTheme } from '../charts/useChartTheme';

const props = defineProps({
  data: { type: Object, required: true },
  pipelineId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const router = useRouter();
const theme = useChartTheme();

const stages = computed(() => props.data.stages || []);

const goToPipelineDeals = stageId => {
  router.push({
    name: 'deals_pipeline',
    params: { pipelineId: props.pipelineId },
    query: { stage_id: stageId },
  });
};

const formatPercent = value =>
  value === null || value === undefined ? '—' : `${value}%`;

const chartCollection = computed(() => ({
  labels: stages.value.map(stage => stage.name),
  datasets: [
    barDataset({
      data: stages.value.map(stage => stage.count),
      label: t('DASHBOARD.COMERCIAL.FUNNEL.COUNT'),
    }),
  ],
}));

// Contagem de negociações: o eixo só marca números inteiros.
const chartOptions = computed(() => {
  const options = barOptions({ theme: theme.value, formatTick: String });
  options.scales.x.ticks.precision = 0;
  return options;
});

const chartHeight = computed(() => `${stages.value.length * 44 + 24}px`);
</script>

<template>
  <div>
    <p v-if="!stages.length" class="py-2 text-sm text-n-slate-10">
      {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
    </p>
    <template v-else>
      <div class="mb-4" :style="{ height: chartHeight }">
        <BarChart :collection="chartCollection" :chart-options="chartOptions" />
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10">
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.FUNNEL.STAGE')"
                  :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.FUNNEL_STAGE')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.FUNNEL.COUNT')"
                  :tooltip="t('DASHBOARD.COMERCIAL.TOOLTIPS.FUNNEL_COUNT')"
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.FUNNEL.CONVERSION_PREVIOUS')"
                  :tooltip="
                    t('DASHBOARD.COMERCIAL.TOOLTIPS.FUNNEL_CONVERSION_PREVIOUS')
                  "
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="
                    t('DASHBOARD.COMERCIAL.FUNNEL.CONVERSION_ACCUMULATED')
                  "
                  :tooltip="
                    t(
                      'DASHBOARD.COMERCIAL.TOOLTIPS.FUNNEL_CONVERSION_ACCUMULATED'
                    )
                  "
                />
              </th>
              <th class="py-1 pr-3">
                <TableHeaderCell
                  :label="t('DASHBOARD.COMERCIAL.FUNNEL.AVERAGE_TIME')"
                  :tooltip="
                    t('DASHBOARD.COMERCIAL.TOOLTIPS.FUNNEL_AVERAGE_TIME')
                  "
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="stage in stages"
              :key="stage.id"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="goToPipelineDeals(stage.id)"
            >
              <td class="py-1 pr-3 text-n-slate-12">{{ stage.name }}</td>
              <td class="py-1 pr-3">{{ stage.count }}</td>
              <td class="py-1 pr-3">
                {{ formatPercent(stage.conversion_from_previous) }}
              </td>
              <td class="py-1 pr-3">
                {{ formatPercent(stage.conversion_accumulated) }}
              </td>
              <td class="py-1 pr-3">
                {{ formatDuration(stage.average_time_in_stage_seconds) }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="py-1 pr-3 text-n-slate-12">
                {{ t('DASHBOARD.COMERCIAL.FUNNEL.WON') }}
              </td>
              <td class="py-1 pr-3">{{ data.won_count }}</td>
              <td class="py-1 pr-3" colspan="3" />
            </tr>
            <tr class="border-t border-n-weak">
              <td class="py-1 pr-3 text-n-slate-12">
                {{ t('DASHBOARD.COMERCIAL.FUNNEL.LOST') }}
              </td>
              <td class="py-1 pr-3">{{ data.lost_count }}</td>
              <td class="py-1 pr-3" colspan="3" />
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
